import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../services/api/apiService';
import { getAllSports } from '../../../services/api/sportsService';

/**
 * Custom hook for managing league list data and operations
 * Handles fetching, creating, updating, and deleting leagues
 *
 * @param {object} [queryParams={}] - Optional server-side filter params
 *   (sport_types, lat, lng, radius) from UserFiltersContext
 */
// skipList: no location yet, so list nothing rather than every region's leagues
export const useLeagueData = (queryParams = {}, isReady = true, { skipList = false } = {}) => {
  const [leagues, setLeagues] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all leagues — re-runs whenever queryParams change
  const fetchLeagues = useCallback(async (overrideParams) => {
    if (skipList && !overrideParams) {
      setLeagues([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = overrideParams ?? queryParams;
      const response = await apiClient.get('/leagues', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      setLeagues(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching leagues:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queryParams), skipList]);

  // Fetch all platform sports for the filter dropdown — always use getAllSports
  // so the dropdown reflects the full platform catalogue, not just the user's
  // registered sports.
  const fetchSports = useCallback(async () => {
    try {
      const allRes = await getAllSports();
      const sportsData = allRes?.data || [];
      setSports(Array.isArray(sportsData) ? sportsData : []);
    } catch (error) {
      // non-critical — filter just won't show sport options
    }
  }, []);

  // Fetch single league by ID
  const fetchLeagueById = useCallback(async (leagueId) => {
    try {
      const response = await apiClient.get(`/leagues/${leagueId}`);
      return response.data?.data;
    } catch (error) {
      console.error('Error fetching league:', error);
      return null;
    }
  }, []);

  // Create new league
  const createLeague = useCallback(async (leagueData) => {
    try {
      const response = await apiClient.post('/leagues', leagueData);
      if (response.data?.data) {
        await fetchLeagues(); // Refresh league list
        return response.data.data;
      }
    } catch (error) {
      throw error;
    }
  }, [fetchLeagues]);

  // Update league
  const updateLeague = useCallback(async (leagueId, leagueData) => {
    try {
      const response = await apiClient.put(`/leagues/${leagueId}`, leagueData);
      if (response.data?.data) {
        await fetchLeagues(); // Refresh league list
        return response.data.data;
      }
    } catch (error) {
      throw error;
    }
  }, [fetchLeagues]);

  // Delete league
  const deleteLeague = useCallback(async (leagueId) => {
    try {
      await apiClient.delete(`/leagues/${leagueId}`);
      await fetchLeagues(); // Refresh league list
    } catch (error) {
      throw error;
    }
  }, [fetchLeagues]);

  // Update league status — uses the dedicated /cancel endpoint to cascade
  const updateLeagueStatus = useCallback(async (leagueId, status) => {
    try {
      let response;
      if (status === 'cancelled') {
        // Cascade: cancel all non-completed matches inside the league
        response = await apiClient.post(`/leagues/${leagueId}/cancel`);
      } else {
        response = await apiClient.put(`/leagues/${leagueId}`, { status });
      }
      await fetchLeagues(); // Refresh league list
      return response.data?.data?.league || response.data?.data;
    } catch (error) {
      throw error;
    }
  }, [fetchLeagues]);

  // Re-fetch whenever filter params change (but only after filters are ready)
  useEffect(() => {
    if (!isReady) return;
    fetchLeagues();
    fetchSports();

    // Check URL for league ID
    const urlPath = window.location.pathname;
    const leagueIdMatch = urlPath.match(/\/leagues\/(\d+)/);
    if (leagueIdMatch) {
      const leagueId = parseInt(leagueIdMatch[1]);
      fetchLeagueById(leagueId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLeagues, isReady]);

  return {
    leagues,
    setLeagues,
    sports,
    setSports,
    loading,
    fetchLeagues,
    fetchSports,
    fetchLeagueById,
    createLeague,
    updateLeague,
    deleteLeague,
    updateLeagueStatus
  };
};
